import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useTerminology from '@sift/skipper/helpers/useTerminology';

// Components
import DragHandleIcon from '@material-ui/icons/DragHandle';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import AttributeRow from './AttributeRow';
import Table from 'components/Table';
import TableHead from 'components/Table/TableHead';
import TableCell from 'components/Table/TableCell';
import TableBody from '@material-ui/core/TableBody';
import Typography from '@material-ui/core/Typography';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import Tooltip from '@material-ui/core/Tooltip';

// Style
import makeStyles from '@material-ui/core/styles/makeStyles';

function SubAttributesTable({ list, editPriorityList }) {
  const classes = useStyles();
  const generalField = list && list.find(item => item && item.objectKey.includes('general'));
  const [newList, setNewList] = useState(null);
  useEffect(() => {
    setNewList(list && list.filter(item => item && !item.objectKey.includes('general')));
  }, [list]);
  const terminology = useTerminology();

  const dragHandle = SortableHandle(() => {
    return <DragHandleIcon className={classes.drag} data-testid="drag handle" />;
  });

  const sortableItem = SortableElement(({ value, dataIndex }) => {
    const DragHandle = dragHandle;
    return (
      <AttributeRow
        classes={classes}
        value={value}
        dragIcon={<DragHandle />}
        isLast={dataIndex === newList.length - 1}
        list={newList}
      />
    );
  });

  const sortList = SortableContainer(({ items }) => {
    const SortableItem = sortableItem;
    const SortableItems = items.map((value, index) => {
      return <SortableItem key={`item-${index}`} index={index} dataIndex={index} value={value} />;
    });

    return (
      <Table classes={{ root: classes.table }}>
        <TableHead>
          <TableCell className={classes.mainCell} variant="head">
            <Typography className={classes.tableHead}>Name</Typography>
          </TableCell>
          <TableCell className={classes.mainCell} variant="head">
            <Typography className={classes.tableHead}>Data Type</Typography>
          </TableCell>
          <TableCell className={classes.mainCell} variant="head">
            <div className={classes.inline}>
              <Typography className={classes.tableHead}>User Editing</Typography>
              <Tooltip
                data-testid="user editing tooltip"
                title={`If ${terminology.plural} can edit the attribute. Partly editable means they can edit this if their profile is not mapped to a data source.`}
              >
                <InfoIcon className={classes.icon} />
              </Tooltip>
            </div>
          </TableCell>
          <TableCell className={classes.mainCell} variant="head">
            <div className={classes.inline}>
              <Typography className={classes.tableHead}>Enabled</Typography>
              <Tooltip
                data-testid="enabled tooltip"
                title="If the attribute is visible anywhere on Sift."
              >
                <InfoIcon className={classes.icon} />
              </Tooltip>
            </div>
          </TableCell>
          <TableCell className={classes.lastCell} variant="head"></TableCell>
        </TableHead>
        <TableBody>
          <AttributeRow
            classes={classes}
            list={[generalField]}
            value={generalField}
            isLast={true}
          />
          {SortableItems}
        </TableBody>
      </Table>
    );
  });

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const list = arrayMove(newList, oldIndex, newIndex);
      setNewList(list);
      editPriorityList(list);
    }
  };
  const SortableListElem = sortList;
  if (!newList) return null;

  return (
    <SortableListElem lockToContainerEdges items={newList} onSortEnd={onSortEnd} useDragHandle />
  );
}

const useStyles = makeStyles(theme => ({
  drag: {
    marginTop: -1,
    cursor: 'move',
    color: theme.colors.gray1,
    width: '100%',
  },
  table: {
    marginBottom: theme.spacing(3),
  },
  icon: {
    marginLeft: theme.spacing(),
    cursor: 'pointer',
    color: theme.palette.text.primary,
  },
  inline: {
    display: 'flex',
    alignItems: 'center',
  },
  tableHead: {
    color: theme.palette.text.primary,
    ...theme.typography.body2Bold,
  },
  mainCell: {
    width: '22.5%',
  },
  lastCell: {
    width: '10%',
    textAlign: 'center',
  },
}));

SubAttributesTable.propTypes = {
  list: PropTypes.array.isRequired,
  editPriorityList: PropTypes.func.isRequired,
};

export default SubAttributesTable;
